import clsx from 'clsx'
import type React from 'react'

import { Button } from '@/ui-components/Button'

export const Pagination = ({
  'aria-label': ariaLabel = 'Page navigation',
  className,
  ...props
}: React.ComponentPropsWithoutRef<'nav'>) => {
  return (
    <nav
      aria-label={ariaLabel}
      {...props}
      className={clsx(className, 'flex gap-x-2')}
    />
  )
}

export const PaginationPrevious = ({
  to = null,
  className,
  children = 'Previous',
  onClick = null,
  disabled = false,
}: React.PropsWithChildren<{
  to?: string | null
  className?: string
  onClick?: (() => void) | null
  disabled?: boolean
}>) => {
  return (
    <span className={clsx(className, 'grow basis-0')}>
      <Button
        {...(to === null ? { disabled: true, className: '!cursor-not-allowed' } : { to })}
        plain
        aria-label="Previous page"
        {...(onClick !== null && { onClick })}
        disabled={disabled || (to === null && onClick === null)}
        className={clsx(to === null && onClick === null && '!cursor-not-allowed')}
      >
        <svg
          className="stroke-current"
          data-slot="icon"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2.75 8H13.25M2.75 8L5.25 5.5M2.75 8L5.25 10.5"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {children}
      </Button>
    </span>
  )
}

export const PaginationNext = ({
  to = null,
  className,
  children = 'Next',
  onClick = null,
  disabled = false,
}: React.PropsWithChildren<{
  to?: string | null
  className?: string
  onClick?: (() => void) | null
  disabled?: boolean
}>) => {
  return (
    <span className={clsx(className, 'flex grow basis-0 justify-end')}>
      <Button
        {...(to === null ? { disabled: true, className: '!cursor-not-allowed' } : { to })}
        plain
        aria-label="Next page"
        {...(onClick !== null && { onClick })}
        disabled={disabled || (to === null && onClick === null)}
        className={clsx(to === null && onClick === null && '!cursor-not-allowed')}
      >
        {children}
        <svg
          className="stroke-current"
          data-slot="icon"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M13.25 8L2.75 8M13.25 8L10.75 10.5M13.25 8L10.75 5.5"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Button>
    </span>
  )
}

export const PaginationList = ({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <span
      {...props}
      className={clsx(className, 'hidden items-baseline gap-x-2 sm:flex')}
    />
  )
}

export const PaginationPage = ({
  to = null,
  className,
  current = false,
  children,
  onClick,
}: React.PropsWithChildren<{ to?: string | null; className?: string; current?: boolean; onClick?: () => void }>) => {
  return (
    <Button
      {...(to !== null && { to })}
      plain
      aria-label={`Page ${children}`}
      aria-current={current ? 'page' : undefined}
      onClick={() => {
        if (current) return
        onClick?.()
      }}
      className={clsx(
        className,
        'min-w-[2.25rem] before:absolute before:-inset-px before:rounded-lg',
        current && 'before:bg-gray-900/5 dark:before:bg-white/10',
      )}
    >
      <span className="-mx-0.5">{children}</span>
    </Button>
  )
}

export const PaginationGap = ({
  className,
  children = <>&hellip;</>,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <span
      aria-hidden="true"
      {...props}
      className={clsx(
        className,
        'w-[2.25rem] text-center text-sm/6 font-semibold text-gray-900 select-none dark:text-white',
      )}
    >
      {children}
    </span>
  )
}
